import { useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
// @mui
import {
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Grid,
  LinearProgress,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
// components
import { MBreadcrumbs } from '../../components/@mui-extend';
import { MotionInView, varFadeInUp } from '../../components/animate';
import Iconify from '../../components/iconify';
import Markdown from '../../components/markdown';
import { Seo, articleSchema, breadcrumbSchema } from '../../components/seo';
import { Widget404 } from '../../components/widegts';
import {
  BlogAuthorCard,
  BlogDetailHero,
  BlogPostNav,
  BlogShareButtons,
  BlogTableOfContents,
  RelatedBlogsWidget,
  syncArticleHeadingIds,
} from '../../sections/web/blog';
import { BlogPostCard } from '../../sections/@dashboard/blog';
import { ContactWidget } from '../../sections/web/home';
// hooks
import useScrollProgress from '../../hooks/useScrollProgress';
// redux
import { useDispatch, useSelector } from '../../redux/store';
import { getBlogPost, getBlogPosts } from '../../redux/slices/blog';
// utils
import { countWords } from '../../utils/blog';

// ----------------------------------------------------------------------

const MORE_LIMIT = 3;

// Reading column stops here so line length stays in the 45-75 character range
// that long-form articles need, whatever the viewport does.
const READING_WIDTH = 760;

const toIsoDate = (date) => (date ? new Date(date).toISOString() : undefined);

function BlogDetailSkeleton() {
  return (
    <Box sx={{ bgcolor: 'background.default' }}>
      <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
        <Skeleton variant="text" width={220} height={24} sx={{ mb: 3 }} />
        <Skeleton variant="rounded" width="100%" height={420} />
        <Skeleton variant="text" width="70%" height={48} sx={{ mt: 3 }} />
        <Skeleton variant="text" width="100%" height={24} />
        <Skeleton variant="text" width="90%" height={24} />
      </Container>
    </Box>
  );
}

// ----------------------------------------------------------------------

export default function BlogPage() {
  const { blogslug } = useParams();
  const dispatch = useDispatch();
  const { post, relatedPosts, posts, isLoading, error } = useSelector((state) => state.blog);

  const [articleNode, setArticleNode] = useState(null);

  // Article plus its related block. The related list also tops up the "more"
  // row further down the page.
  useEffect(() => {
    if (blogslug) {
      dispatch(getBlogPost(blogslug));
    }
  }, [dispatch, blogslug]);

  const articleRef = useRef(null);
  const progress = useScrollProgress(articleRef);

  // Neighbours come from a lightweight catalogue fetch once the article is
  // known, so prev/next walk the same backend ordering as the blogs page.
  useEffect(() => {
    if (post?.category) {
      dispatch(getBlogPosts({ category: post.category, pageSize: 50 }));
    }
  }, [dispatch, post?.category]);

  const { previous, next } = useMemo(() => {
    if (!post || !Array.isArray(posts) || posts.length === 0) {
      return { previous: null, next: null };
    }

    const ordered = [...posts].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    const at = ordered.findIndex((item) => item.slug === post.slug);

    if (at === -1) {
      return { previous: null, next: null };
    }

    return { previous: ordered[at - 1] ?? null, next: ordered[at + 1] ?? null };
  }, [post, posts]);

  // "More from the blog": related first, then the newest catalogue rows that
  // are neither the article nor already shown as related.
  const morePosts = useMemo(() => {
    if (!post) {
      return [];
    }

    const excluded = new Set([post.slug, ...(relatedPosts ?? []).map((item) => item.slug)]);
    const pool = [...(relatedPosts ?? []), ...(Array.isArray(posts) ? posts : [])];

    return pool
      .filter((item) => item && !excluded.has(item.slug))
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .slice(0, MORE_LIMIT);
  }, [post, posts, relatedPosts]);

  // All backend rows render Quill HTML, so the TOC anchors are stamped onto
  // the live heading elements once the article node mounts.
  useEffect(() => {
    if (!articleNode || !post?.content) {
      return;
    }

    // Parse in the same order the TOC does: rendered h2/h3 tags.
    const doc = new DOMParser().parseFromString(post.content, 'text/html');
    const ids = [...doc.querySelectorAll('h2, h3')]
      .map((node) =>
        (node.textContent ?? '')
          .toLowerCase()
          .trim()
          .replace(/[^a-z0-9\s-]/g, '')
          .replace(/\s+/g, '-')
          .replace(/^-+|-+$/g, '')
      )
      .filter(Boolean);

    syncArticleHeadingIds(articleNode, ids.map((id, index) => ({ id: `${id}`, key: index })));
  }, [articleNode, post?.content]);

  // Honour an in-page anchor so a shared section link opens on that section.
  useEffect(() => {
    if (!post) {
      return;
    }

    const { hash } = window.location;
    if (!hash) {
      return;
    }

    const target = document.getElementById(decodeURIComponent(hash.slice(1)));
    if (target) {
      window.setTimeout(() => target.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
    }
  }, [post]);

  if (isLoading && !post) {
    return <BlogDetailSkeleton />;
  }

  if (!post) {
    // A failed fetch surfaces the backend message; an unknown slug keeps the
    // plain not-found copy.
    const subitem = error ? 'We could not load this article. Please try again later.' : `No article found for "${blogslug}"`;

    return <Widget404 item="Article" subitem={subitem} />;
  }

  const { slug, title, excerpt, cover, category, keywords, createdAt, updatedAt, author } = post;
  const authorName = author?.name ?? 'ASAP UAE';

  const jsonLd = [
    articleSchema({
      headline: title,
      description: excerpt,
      image: cover,
      path: `/blog/${slug}`,
      datePublished: toIsoDate(createdAt),
      dateModified: toIsoDate(updatedAt ?? createdAt),
      authorName,
      authorJobTitle: author?.role,
      section: category,
      keywords: (keywords ?? []).join(', '),
      wordCount: countWords(post.content),
    }),
    breadcrumbSchema([
      { name: 'Home', path: '/' },
      { name: 'Blog', path: '/blogs' },
      { name: title, path: `/blog/${slug}` },
    ]),
  ];

  return (
    <>
      <Seo
        title={title}
        description={excerpt}
        path={`/blog/${slug}`}
        image={cover}
        type="article"
        jsonLd={jsonLd}
      >
        <meta property="article:section" content={category} />
        <meta property="article:published_time" content={toIsoDate(createdAt)} />
        <meta property="article:modified_time" content={toIsoDate(updatedAt ?? createdAt)} />
      </Seo>

      <LinearProgress
        variant="determinate"
        value={progress}
        aria-label="Reading progress"
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: (theme) => theme.zIndex.appBar + 1,
          height: 3,
          '& .MuiLinearProgress-bar': { bgcolor: 'primary.main' },
        }}
      />

      <Box sx={{ bgcolor: 'background.default' }}>
        <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
          <MBreadcrumbs
            links={[
              { name: 'Home', href: '/' },
              { name: 'Blog', href: '/blogs' },
              { name: title },
            ]}
            sx={{ mb: { xs: 2.5, md: 4 } }}
          />

          <BlogDetailHero post={post} />

          <Grid container spacing={{ xs: 4, md: 5 }} sx={{ mt: { xs: 1, md: 2 } }}>
            {/* Article — three quarters of the row */}
            <Grid size={{ xs: 12, md: 9 }}>
              <MotionInView variants={varFadeInUp}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  sx={{ pb: 3, borderBottom: (theme) => `1px solid ${theme.palette.divider}`, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between' }}
                >
                  <BlogShareButtons post={post} />

                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', alignItems: 'center' }}>
                    {(keywords ?? []).map((keyword) => (
                      <Chip
                        key={keyword}
                        component={RouterLink}
                        to={`/blogs?q=${encodeURIComponent(keyword)}`}
                        clickable
                        size="small"
                        label={keyword}
                        sx={{ fontSize: 12, color: 'text.secondary' }}
                      />
                    ))}
                  </Stack>
                </Stack>
              </MotionInView>

              {/* Table of contents — collapsible on small screens */}
              <Box sx={{ display: { xs: 'block', md: 'none' }, mt: 2.5 }}>
                <BlogTableOfContents content={post.content} variant="inline" />
              </Box>

              <MotionInView variants={varFadeInUp}>
                <Box
                  ref={(node) => {
                    articleRef.current = node;
                    setArticleNode(node);
                  }}
                  sx={{
                    maxWidth: READING_WIDTH,
                    mx: { xs: 0, md: 'auto' },
                    pb: { xs: 3, md: 4 },
                    fontSize: { xs: 16, md: 17 },
                  }}
                >
                  <Markdown>{post.content}</Markdown>
                </Box>
              </MotionInView>

              <Divider sx={{ my: { xs: 3, md: 4 } }} />

              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                sx={{ alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between' }}
              >
                <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
                  Found this useful?
                </Typography>
                <BlogShareButtons post={post} align="right" />
              </Stack>

              <BlogAuthorCard post={post} articleCount={0} />

              <BlogPostNav previous={previous} next={next} />
            </Grid>

            {/* Related blogs — one quarter of the row */}
            <Grid size={{ xs: 12, md: 3 }}>
              <Stack spacing={2.5}>
                <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                  <BlogTableOfContents content={post.content} variant="sidebar" />
                </Box>
                <RelatedBlogsWidget posts={relatedPosts} category={category} />
              </Stack>
            </Grid>
          </Grid>

          {morePosts.length > 0 && (
            <Box sx={{ mt: { xs: 6, md: 9 } }}>
              <Divider sx={{ mb: { xs: 4, md: 5 } }} />

              <MotionInView variants={varFadeInUp}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  sx={{ mb: { xs: 3, md: 4 }, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between' }}
                >
                  <Box>
                    <Typography variant="overline" sx={{ color: '#DF3E30', fontWeight: 700, letterSpacing: 1.2 }}>
                      Keep reading
                    </Typography>
                    <Typography component="h2" variant="h4" sx={{ color: 'text.primary' }}>
                      More from the blog
                    </Typography>
                  </Box>

                  <Button
                    component={RouterLink}
                    to="/blogs"
                    variant="outlined"
                    endIcon={<Iconify icon="eva:arrow-forward-outline" />}
                  >
                    All articles
                  </Button>
                </Stack>
              </MotionInView>

              <Grid container spacing={{ xs: 3, md: 4 }} sx={{ alignItems: 'stretch' }}>
                {morePosts.map((item, index) => (
                  <BlogPostCard key={item.slug} post={item} index={index} />
                ))}
              </Grid>
            </Box>
          )}

          <Box sx={{ mt: { xs: 6, md: 8 } }}>
            <ContactWidget />
          </Box>
        </Container>
      </Box>
    </>
  );
}
